import { Box, IconButton, Typography } from '@mui/material'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { VehiclePhotoDialogDispatchContext } from '.'
import {
  convertDateToString,
  getSidePhotoKey,
  checkIsBase64,
} from '../../lib/helpers'
import { PhotoTitle, PhotoWrapper } from './styled/PhotoTitle'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import PhotoLabel from './styled/PhotoLabel'
import LoadableImage from '../LoadableImage'
import { History } from '../../constants'

const PhotosList = ({
  in_route,
  dateToday,
  photo,
  photos,
  fullPhotos = [],
  sideKey,
}: {
  in_route: boolean
  dateToday: string
  photo: Partial<History> | null
  photos: History[]
  fullPhotos: History[]
  sideKey: string
}) => {
  const { t } = useTranslation()

  const vehiclePhotoContextDispatch = useContext(
    VehiclePhotoDialogDispatchContext
  )

  return (
    <>
      <PhotoTitle variant="h1" component="p">
        {t('vehicle_photos.list_title')}
      </PhotoTitle>
      <Box width="100%" display="flex" overflow="auto">
        {!in_route &&
          (photo && dateToday ? (
            <PhotoWrapper
              onClick={() => {
                vehiclePhotoContextDispatch.setVehiclePhotoPreviewDialog({
                  open: true,
                  photo: `data:image/png;base64,${photo}`,
                  timestamp: t('common.today'),
                })
              }}
            >
              <Box
                component="img"
                src={
                  checkIsBase64(photo)
                    ? `data:image/png;base64,${photo}`
                    : photo
                }
                sx={{ width: '100%' }}
                alt={`This is taken by ${dateToday}`}
              />
              <PhotoLabel
                withPhotoLabelToday
                onClick={(e) => e.stopPropagation()}
              >
                <Typography variant="body1">
                  {convertDateToString(dateToday, true)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    vehiclePhotoContextDispatch.setVehiclePhotoDialog({
                      open: true,
                    })
                  }
                >
                  <EditIcon />
                </IconButton>
              </PhotoLabel>
            </PhotoWrapper>
          ) : (
            <Box
              component="label"
              htmlFor="upload-photo-img"
              className="photo-wrapper-add"
              sx={(theme) => ({
                position: 'relative',
                '& + div': {
                  marginLeft: theme.spacing(1),
                },
                width: 150,
                height: 208,
                padding: theme.spacing(0.625),
              })}
            >
              <PhotoWrapper withAddIconStyles>
                <AddIcon color="primary" />
              </PhotoWrapper>
            </Box>
          ))}
        {photos.map((photo) => {
          // Take the blobId from the photo object
          // side key is left, right ...
          /// the prop is photoLeft so we need to get the key from the sideKey
          const currentSideKey = getSidePhotoKey(sideKey) as keyof typeof photo
          const currentSideKeyForThumbnail = `${currentSideKey}Thumb`
          const thumbnailBlobId = photo[currentSideKeyForThumbnail] as string
          const fullPhoto = fullPhotos.find(
            (fullPhoto) => fullPhoto.id === photo.id
          ) as History

          const fullPhotoBlobId = fullPhoto?.[currentSideKey] as string
          // If there is no thumbnail or full photo, skip this photo
          // This happens because we're getting photos and don't know the side key
          // so we need to check if the photo has the blobId for the current side
          // if not, we skip this photo
          if (!thumbnailBlobId || !fullPhotoBlobId) {
            return null
          }
          return (
            <PhotoWrapper
              key={photo.id}
              onClick={() => {
                const timestamp = photo.timestamp
                vehiclePhotoContextDispatch.setVehiclePhotoPreviewDialog({
                  open: true,
                  photo: fullPhotoBlobId,
                  timestamp: timestamp ? convertDateToString(timestamp) : '',
                })
              }}
            >
              <LoadableImage
                blobId={thumbnailBlobId}
                sx={{ width: '100%' }}
                alt={`This is taken by ${photo.timestamp}`}
              />
              <PhotoLabel>
                <Typography variant="body1">
                  {convertDateToString(photo.timestamp)}
                </Typography>
              </PhotoLabel>
            </PhotoWrapper>
          )
        })}
      </Box>
    </>
  )
}

export default PhotosList
